import type { SurfScoredDay } from "./detail";
import {
  safetyStateForIndicators,
  type ConditionSafetyState,
} from "./safety-notes";
import type { SupProfileKey } from "./sport-profiles";

const CONDITION_SAFETY_STATES = new Set<ConditionSafetyState>([
  "clear",
  "flagged-amber",
  "flagged-red",
  "no-data",
]);

/** Read only one profile's exact day-level safety. Missing evidence stays
 * unavailable and never falls back to generic SUP or Surf safety. */
export function namedSupForecastSafetyState(
  day: Pick<SurfScoredDay, "safetyState" | "sportSafety">,
  profile: SupProfileKey,
): ConditionSafetyState {
  const persisted = day.safetyState?.[profile];
  if (persisted && CONDITION_SAFETY_STATES.has(persisted)) return persisted;

  const indicators = day.sportSafety?.[profile];
  return Array.isArray(indicators)
    ? safetyStateForIndicators(indicators)
    : "no-data";
}

/** Select the strongest exact named forecast day that is not canonically red.
 * Amber and unavailable evidence remain eligible; an all-red or empty forecast
 * has no promoted best day. */
export function selectNamedSupBestForecastDate(
  forecast: readonly SurfScoredDay[],
  profile: SupProfileKey,
): string | null {
  let best: SurfScoredDay | null = null;
  for (const day of forecast) {
    if (!Number.isFinite(day.score) ||
        namedSupForecastSafetyState(day, profile) === "flagged-red") {
      continue;
    }
    if (!best || day.score > best.score) best = day;
  }
  return best?.date ?? null;
}
